import { Capacitor, registerPlugin } from '@capacitor/core'
import { App } from '@capacitor/app'

export const isNativeAudio =
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'

const Native = isNativeAudio ? registerPlugin('NativeAudio') : null

const subs = new Map()   // audio id -> NativeAudioElement
const positionStore = new Map() // audio id -> last playback position in seconds
let currentId = null     // the element that currently owns the native player
let queueOwner = null
let nativeId = null
let nativePlaying = false
let trackTimer = null
const plain = (x) => JSON.parse(JSON.stringify(x))

if (Native) {
  Native.addListener('time', ({ id, position, duration }) => {
    if (id !== currentId) return
    subs.get(id)?._onTime(position / 1000, duration / 1000)
  })

  Native.addListener('queueEnded', () =>
    window.dispatchEvent(new CustomEvent('native:queue-ended'))
  )

  Native.addListener('trackChanged', ({ audioId }) => {
    nativeId = audioId; nativePlaying = true; currentId = audioId
    clearTimeout(trackTimer)
    trackTimer = setTimeout(() => window.dispatchEvent(
      new CustomEvent('native:track-changed', { detail: { id: nativeId } })), 150)
  })

  Native.addListener('remote', ({ action, value }) => {
    if (action === 'onPlay') nativePlaying = true
    if (action === 'onPause') nativePlaying = false
    window.__mediabridge?.[action]?.(value)
  })

  App.addListener('appStateChange', async ({ isActive }) => {
    if (!isActive) return
    const s = await NativeAudio.getState()
    if (!s?.id) return
    nativeId = s.id; nativePlaying = !!s.isPlaying; currentId = s.id
    window.dispatchEvent(new CustomEvent('native:track-changed', { detail: { id: s.id } }))
  })
}

export const NativeAudio = {
  setQueue(owner, items, startIndex) {
    queueOwner = owner
    const plainItems = plain(items)
    return Native?.setQueue({ owner, items: plainItems, startIndex }).catch(e => console.warn('[NativeAudio] setQueue error:', e))
  },
  appendQueue: (items) => {
    const plainItems = plain(items)
    return Native?.appendQueue({ items: plainItems }).catch(e => console.warn('[NativeAudio] appendQueue error:', e))
  },
  ownsQueue: (owner) => queueOwner === owner,
  getState: () => Native?.getState().catch(() => null),
  playId: (id, startMs = 0, url = '', title = '', artist = '', cover = '') =>
    Native?.playId({ id, startMs, url, title, artist, cover }).catch(e => console.warn('[NativeAudio] playId error:', e)),
  preload: (items, bytes = 80000) => Native?.preload({ items: plain(items), bytes }).catch(e => console.warn('[NativeAudio] preload error:', e)),
  pause: () => Native?.pause().catch(e => console.warn('[NativeAudio] pause error:', e)),
  stop: () => Native?.stop().catch(e => console.warn('[NativeAudio] stop error:', e)),
  seekTo: (id, position) => Native?.seekTo({ id, position }).catch(e => console.warn('[NativeAudio] seekTo error:', e)),
  setSpeed: (speed) => Native?.setSpeed({ speed }).catch(e => console.warn('[NativeAudio] setSpeed error:', e)),
  isCached: (url) => Native?.isCached({ url }).then(r => r.cached).catch(() => false),
  cacheTrack: (url) => Native?.cacheTrack({ url }).then(r => r.success).catch(() => false),
  unsaveTrack: (url) => Native?.unsaveTrack({ url }).then(r => r.success).catch(() => false),
}

/** Mimics the parts of HTMLAudioElement that Publication.vue uses. */
export class NativeAudioElement {
  constructor(id, metaProvider) {
    this.id = id
    this.metaProvider = metaProvider
    this.preload = 'none'
    this._src = ''
    this._pos = positionStore.get(id) || 0
    this._dur = 0
    this._rate = 1
    this._l = {}
    this._seekTimer = null
    this._seekTarget = 0
    this._holdUntil = 0
  }
  get src() { return this._src }
  set src(v) { this._src = v }
  get duration() { return this._dur || NaN }
  get currentTime() { return this._pos }
  set currentTime(t) {
    this._pos = t; this._seekTarget = t
    positionStore.set(this.id, t)
    this._holdUntil = Date.now() + 400
    if (this._seekTimer) return
    this._seekTimer = setTimeout(() => {
      this._seekTimer = null
      if (currentId === this.id)
        NativeAudio.seekTo(this.id, Math.round(this._seekTarget * 1000))
    }, 120)
  }
  get playbackRate() { return this._rate }
  set playbackRate(r) {
    this._rate = r
    if (currentId === this.id) NativeAudio.setSpeed(r)
  }
  addEventListener(name, fn) { this._l[name] = fn }

  play() {
    currentId = this.id
    subs.set(this.id, this)
    if (nativeId === this.id && nativePlaying) return Promise.resolve()
    nativeId = this.id; nativePlaying = true
    const meta = this.metaProvider?.() || {}
    NativeAudio.playId(this.id, 0, this._src, meta.title || '', meta.artist || '', meta.artwork || '')
    return Promise.resolve()
  }
  pause()   { if (currentId === this.id) { nativePlaying = false; NativeAudio.pause() } }
  dispose() {
    clearTimeout(this._seekTimer)
    subs.delete(this.id)
  }

  _onTime(pos, dur) {
    this._dur = dur
    positionStore.set(this.id, pos)
    if (Date.now() < this._holdUntil) return
    this._pos = pos
    this._l.timeupdate?.()
  }
  _onEnded() {
    this._pos = 0
    positionStore.delete(this.id)
    this._l.ended?.()
  }
  _onError() { window.__mediabridge?.onStop?.() }
}
